import { Extension } from '@speckle/viewer'
import * as THREE from 'three'
import { Copc, Hierarchy } from 'copc'

export interface CopcExtensionParams {
    url: string
    pointSize?: number
}

/**
 * Extension to load and render COPC point clouds within the Speckle Viewer
 */
export class CopcExtension extends Extension {
    private url: string
    private pointSize: number
    private pointsList: THREE.Points[] = []
    public pointCloudGroup: THREE.Group = new THREE.Group()
    private copc: Copc | null = null
    private baseCenter: {x: number, y: number, z: number} | null = null

    public get id() {
        return 'CopcExtension'
    }

    constructor(viewer: any) {
        super(viewer)
        this.url = ''
        this.pointSize = 1
    }

    public async onInit() {
        console.log('CopcExtension initialized')
        
        // Add the point cloud group to the Speckle scene
        const renderer = (this.viewer as any).getRenderer()
        if (renderer && renderer.scene) {
            renderer.scene.add(this.pointCloudGroup)
        }
    }

    public async setConfiguration(params: CopcExtensionParams) {
        this.url = params.url
        this.pointSize = params.pointSize || 1
        console.log('Configuring CopcExtension with URL:', this.url)
        try {
            await this.loadCopc()
        } catch (error) {
            console.error('Failed to load COPC file:', error)
        }
    }

    private async loadCopc() {
        // Fetch the COPC file
        this.copc = await Copc.create(this.url)
        const { header } = this.copc

        console.log('COPC metadata loaded')

        // Load initial points
        const hierarchy = await Hierarchy.load(this.url, (this.copc as any).info.rootHierarchyPage)

        // Stream all nodes dynamically
        const keys = Object.keys((hierarchy as any).nodes)
        console.log(`Streaming ${keys.length} nodes from COPC...`)

        for (const key of keys) {
            const node = (hierarchy as any).nodes[key]
            
            try {
                // Load points for the current node
                const view = await Copc.loadPointDataView(this.url, this.copc, node)

                const positions: number[] = []
                const colors: number[] = []

                const getX = view.getter('X')
                const getY = view.getter('Y')
                const getZ = view.getter('Z')
                
                const hasColor = 'Red' in view.dimensions && 'Green' in view.dimensions && 'Blue' in view.dimensions
                const getR = hasColor ? view.getter('Red') : null
                const getG = hasColor ? view.getter('Green') : null
                const getB = hasColor ? view.getter('Blue') : null

                const scaleX = header.scale[0]
                const scaleY = header.scale[1]
                const scaleZ = header.scale[2]

                const offsetX = header.offset[0]
                const offsetY = header.offset[1]
                const offsetZ = header.offset[2]

                // Extraction loop
                for (let i = 0; i < view.pointCount; i++) {
                    const x = getX(i) * scaleX + offsetX
                    const y = getY(i) * scaleY + offsetY
                    const z = getZ(i) * scaleZ + offsetZ

                    positions.push(x, y, z)

                    if (hasColor && getR && getG && getB) {
                        colors.push(getR(i) / 65535, getG(i) / 65535, getB(i) / 65535)
                    } else {
                        colors.push(1, 1, 1)
                    }
                }

                // Add points chunk to scene
                this.addPointCloudNode(positions, colors)
            } catch (e) {
                console.error(`Error loading COPC node ${key}:`, e)
            }
        }
    }

    private addPointCloudNode(positions: number[], colors: number[]) {
        if (positions.length < 3) return;

        // Geometrically center the points to bypass Float32 precision limits in WebGL
        if (!this.baseCenter) {
            this.baseCenter = { 
                x: positions[0] as number, 
                y: positions[1] as number, 
                z: positions[2] as number 
            }
        }

        const center = this.baseCenter!
        for (let i = 0; i < positions.length; i += 3) {
            positions[i] -= center.x
            positions[i+1] -= center.y
            positions[i+2] -= center.z
        }

        const geometry = new THREE.BufferGeometry()
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))
        
        geometry.computeBoundingBox()
        geometry.computeBoundingSphere()

        const material = new THREE.PointsMaterial({
            size: this.pointSize,
            vertexColors: true,
            sizeAttenuation: true, // Revert to standard attenuation
            depthTest: true,
            depthWrite: true,
            transparent: false
        })
        
        const pointsChunk = new THREE.Points(geometry, material)
        pointsChunk.frustumCulled = false
        
        // VITAL FIX: Force Speckle renderer passes to see this object without rendering it in the top overlay
        pointsChunk.layers.set(1) // ObjectLayers.STREAM_CONTENT = 1
        this.pointsList.push(pointsChunk)

        // Add to Speckle scene through the group
        this.pointCloudGroup.add(pointsChunk)
        const renderer = (this.viewer as any).getRenderer()
        if (renderer && renderer.scene) {
            if (typeof this.viewer.requestRender === 'function') {
                this.viewer.requestRender()
            }
        }
    }

    public onUpdate() {
        // Optional: handle dynamic updates if needed
    }

    public onDestroy() {
        if (this.pointsList.length > 0) {
            const renderer = (this.viewer as any).getRenderer()
            if (renderer && renderer.scene) {
                renderer.scene.remove(this.pointCloudGroup)
            }
            for (const points of this.pointsList) {
                points.geometry.dispose()
                if (Array.isArray(points.material)) {
                    points.material.forEach((m: any) => m.dispose())
                } else {
                    points.material.dispose()
                }
            }
            this.pointsList = []
            this.pointCloudGroup.clear()
        }
    }
}
